/**
 * Hand-drawn postmark used as the brand mark on the login page.
 * Two-arc curved text so the bottom is right-side-up when read from outside
 * (the classic SVG textPath gotcha: a single-circle textPath flips letters
 * upside down on the bottom half).
 */
export function Postmark({ size = 116 }: { size?: number }) {
  return (
    <svg
      className="postmark"
      width={size}
      height={size}
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        {/* Top arc: from (18,100) curving up and over to (182,100).
            Text drawn along this arc reads left-to-right across the top. */}
        <path
          id="pm-top"
          d="M 18 100 A 82 82 0 0 1 182 100"
          fill="none"
        />

        {/* Bottom arc: from (182,100) curving DOWN and under to (18,100).
            Going clockwise-from-the-right means letters at 6 o'clock have their
            tops pointing away from center → upright when viewed from outside. */}
        <path
          id="pm-bottom"
          d="M 182 100 A 82 82 0 0 1 18 100"
          fill="none"
        />
      </defs>

      {/* Three concentric rings */}
      <circle cx="100" cy="100" r="92" fill="none" stroke="#16213d" strokeWidth="1.5" />
      <circle cx="100" cy="100" r="72" fill="none" stroke="#16213d" strokeWidth="1" />
      <circle cx="100" cy="100" r="44" fill="none" stroke="#16213d" strokeWidth="0.8" />

      {/* Cancellation tick marks every 30° in the outer band */}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = 100 + 78 * Math.cos(rad);
        const y1 = 100 + 78 * Math.sin(rad);
        const x2 = 100 + 86 * Math.cos(rad);
        const y2 = 100 + 86 * Math.sin(rad);
        return (
          <line
            key={deg}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#16213d"
            strokeWidth="1"
            opacity="0.7"
          />
        );
      })}

      {/* Top curved text — reads left-to-right across the top */}
      <text
        fontFamily="JetBrains Mono, monospace"
        fontSize="8.5"
        fontWeight="600"
        fill="#16213d"
        letterSpacing="3"
      >
        <textPath href="#pm-top" startOffset="50%" textAnchor="middle">
          MAILMEBACK ⋆ OUTLOOK → HUBSPOT
        </textPath>
      </text>

      {/* Bottom curved text — second arc traversed the other way so letters
          stay upright when viewed from outside the postmark */}
      <text
        fontFamily="JetBrains Mono, monospace"
        fontSize="8.5"
        fontWeight="600"
        fill="#16213d"
        letterSpacing="3"
      >
        <textPath href="#pm-bottom" startOffset="50%" textAnchor="middle">
          EST. 2026 ⋆ FIRST CLASS
        </textPath>
      </text>

      {/* Center italic Fraunces "M" monogram */}
      <text
        x="100"
        y="116"
        textAnchor="middle"
        fontFamily="Fraunces, Georgia, serif"
        fontSize="52"
        fontStyle="italic"
        fontWeight="400"
        fill="#16213d"
      >
        M
      </text>
    </svg>
  );
}
